/**
 * Raids, the first slice (v1 ticket 47): the ticket's types and its eight routes, and nothing else. Every raid
 * request the web makes goes through here, so a field that changes on the server is changed in one place.
 *
 * The client never decides a result: verdicts, damage, bands and glory are read from these replies as they come.
 */
import { j } from '../../api';
import type { FortCounts } from './raid-fort-plan';

export type Form = 'choice' | 'listen' | 'gap' | 'recall' | 'dictation';
export type Material = 'scaffolding' | 'wood' | 'stone' | 'gold';
export type Band = 'held' | 'draw' | 'breach' | 'sacked';
/** An answer as graded: what the learner gave. */
export type Verdict = 'right' | 'scratched' | 'wrong';
/** What a weapon did, in a defence and a report. */
export type Struck = 'blocked' | 'scratched' | 'hit' | 'missed';
export type Side = 'attacker' | 'defender';

export interface Who { id: string; name: string }
/**
 * The district a raid aims at (v1 ticket 48): the attacker reads its slot ("District 3"), the defender their own
 * district's real title. Never both, and the attacker never the title.
 */
export interface Aim { slot?: number; title?: string;
  /** The defender's own district's id, only in the defender's own views (v1 ticket 51); the attacker never gets it. */
  id?: string }
/** A learner's own word, only ever sent to them. */
export interface Shown { text: string; latin?: string; meaning: string }

export interface RaidCard {
  form: Form;
  /** choice: the word to read. */
  word?: { text: string; latin?: string };
  /** choice, listen: 2–4 meanings, shuffled; the answer is its index. */
  options?: string[];
  /** listen, dictation: the word to hear (never shown before the answer), through the listen route of this moment. */
  hear?: { momentId: string; text: string };
  /** gap, recall: what to type. */
  meaning?: string;
  /** gap: the line around the blank. */
  line?: { before: string; after: string; translation?: string };
}

export interface AnswerBody { index: number; choice?: number; given?: string; gaveUp?: true }

export type RaidOpen = 'open' | 'pending' | 'full' | 'no-town';
export interface RaidFriend { id: string; name: string; glory: number; raid: RaidOpen }
export interface RaidReportLine { id: string; side: Side; other: Who; district?: Aim; band: Band; glory: number; resolvedAt: string; unread: boolean }

export interface RaidOverview {
  circle: boolean;
  glory: number;
  friends: RaidFriend[];
  incoming: { id: string; from: Who; district?: Aim; weapons: number; launchedAt: string }[];
  outgoing: { id: string; to: Who; district?: Aim; weapons: number; launchedAt: string }[];
  /** Newest first, at most 20. */
  reports: RaidReportLine[];
  /** An army built and not sent yet. */
  draft?: { id: string; to: Who; district?: Aim };
  launchesLeft: number;
  /** Words left on the field that can be rebuilt today (v1 ticket 49). */
  spoils?: { ready: number };
}

export interface ArmouryForm { form: Form; weight: number; power: number; challenge?: string; step?: number; context?: 'familiar' | 'meaning' | 'new' | 'audio'; recommended?: boolean; card?: RaidCard }
export interface ArmouryWord { key: string; text: string; latin?: string; meaning: string; material: Material; forms: ArmouryForm[]; shaky: boolean;
  /** Immutable proof available to re-add only while editing its preparation. */
  proved?: boolean;
  /** A weapon on this word broke in the last 24 hours: it cannot be loaded until restUntil. */
  resting?: boolean; restUntil?: string; potentialSpent?: number; returnAt?: string | null }
/** One of a friend's districts as the scout shows it: counts only, never a title, word or id. */
export interface ScoutDistrict {
  slot: number; words: number; materials: Record<'fog' | Material, number>; ivy: number; lamps: number;
  streets: number; paved: number;
  condition?: 'maintained' | 'mixed' | 'neglected' | 'unproven';
  /** How many of its words can be hit: the most weapons an army may hold here. 0: it cannot be aimed at. */
  targets: number;
}
export interface Armoury { budget: number; words: ArmouryWord[]; allGold?: boolean; allPotentialSpent?: boolean; emptyArsenal?: boolean; previewOnly?: boolean }

export interface LoadingWeapon { index: number; key: string; form: Form; /** Private server challenge identity; absent on legacy V1 weapons. */ challenge?: string; material: Material; weight: number; power: number; loaded: boolean | null; word: Shown }
/** The attacker's army while its cards are answered (`cards[i]` is null once weapon `i` is answered). */
export interface LoadingRaid { id: string; state: 'loading'; side: 'attacker'; other: Who; district?: Aim; expiresAt?: string; weapons: LoadingWeapon[]; cards: (RaidCard | null)[] }

export interface SentRaid {
  id: string; state: 'pending'; side: 'attacker'; other: Who; district?: Aim; launchedAt: string;
  weapons: { index: number; form: Form; material: Material; power: number; word: Shown }[];
}

export interface DefenceResult { verdict: Struck; damage: number; blocked: number; answer?: Shown }
export interface DefenceWeapon {
  index: number; form: Form; material: Material; power: number;
  /** The form it landed in; null (with `card`) for a weapon that missed. */
  landed: Form | null;
  card: RaidCard | null;
  /** The struck word's key, the defender's own (v1 ticket 51), to find its building in their town. */
  key?: string;
  result?: DefenceResult;
}
/** `fort`: the district's counts, for drawing its fort (v1 ticket 50); absent when the district is gone. */
export interface DefenceRaid { id: string; state: 'pending'; side: 'defender'; other: Who; district?: Aim; fort?: FortCounts; launchedAt: string; weapons: DefenceWeapon[] }

export interface ReportWeapon { index: number; form: Form; material: Material; power: number; verdict: Struck;
  /** Only the defender's report carries damage per weapon: the attacker reads the verdict alone. */
  damage?: number; word?: Shown;
  /** The defender's report only: the struck word's key (v1 ticket 51). */
  key?: string }
export interface RaidReport {
  id: string; state: 'resolved'; side: Side; other: Who; district?: Aim; fort?: FortCounts; band: Band; total: number; damage: number;
  glory: { you: number; them: number };
  /** Nobody answered within a day: the cards left played themselves. */
  auto: boolean;
  resolvedAt: string;
  /** This side has not read the report yet: the raider's client plays the battle first, then acks it. */
  unseen?: boolean;
  /** `word` is the viewer's own: the attacker's weapon, or the defender's word that was struck. */
  weapons: ReportWeapon[];
  /** The attacker's report: every broken weapon, shown the same whatever it left (v1 ticket 49). */
  left?: { index: number; form: Form; material: Material; power: number; word: Shown }[];
  /** The defender's report: the words the raid left on the field. */
  spoils?: { id: string; state: 'waiting' | 'salvaged' | 'planted'; word: Shown }[];
}

/** A raid that was called off (a friend left the circle). The contract names the state only. */
export interface CancelledRaid { reason?: 'rules-changed' | 'expired'; id: string; state: 'cancelled'; side?: Side; other?: Who }

export type Raid = LoadingRaid | SentRaid | DefenceRaid | RaidReport | CancelledRaid;

export interface LoadReply { verdict: Verdict; loaded: boolean; answer: Shown; expiresAt?: string | null; learning?: { saved: boolean } }
export interface LaunchReply { ok: true; weapons: number; power: number }
export interface DefendReply {
  verdict: Verdict; answer: Shown; damage: number; blocked: number; done: boolean; report?: RaidReport;
  learning?: { saved: boolean; recovery?: boolean };
}

/** A word left on the field, ready to rebuild: the meaning and shuffled tiles, never the word. */
export interface Spoil { id: string; from: Who; district?: { title: string }; kind: string; meaning: string; tiles: string[]; sep: string }
export interface SpoilsList { ready: Spoil[]; waiting: number }
export interface RebuildReply { verdict: 'right' | 'wrong'; answer: Shown; trophy?: { from: string } }

/** The POSTs take JSON only, a body even where there is nothing to say. */
const post = (path: string, body: unknown = {}) =>
  fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const at = (id: string) => `/api/raids/${encodeURIComponent(id)}`;

export const raidApi = {
  overview: () => fetch('/api/raids').then((r) => j<RaidOverview>(r)),
  armoury: (edit?: string) => fetch(`/api/raids/armoury${edit ? `?edit=${encodeURIComponent(edit)}` : ''}`).then((r) => j<Armoury>(r)),
  scout: (friend: string) => fetch(`/api/raids/scout/${encodeURIComponent(friend)}`).then((r) => j<{ districts: ScoutDistrict[] }>(r)),
  /** `edit` is the draft being changed: what it had already proved stays proved, and has no card (null) here. */
  create: (to: string, weapons: { key: string; form: Form }[], district: number, edit?: string) =>
    post('/api/raids', { to, weapons, district, ...(edit ? { edit } : {}) }).then((r) => j<{ id: string; cards: (RaidCard | null)[]; expiresAt?: string | null }>(r)),
  load: (id: string, body: AnswerBody) => post(`${at(id)}/load`, body).then((r) => j<LoadReply>(r)),
  launch: (id: string) => post(`${at(id)}/launch`).then((r) => j<LaunchReply>(r)),
  get: (id: string) => fetch(at(id)).then((r) => j<Raid>(r)),
  defend: (id: string, body: AnswerBody) => post(`${at(id)}/defend`, body).then((r) => j<DefendReply>(r)),
  spoils: () => fetch('/api/raids/spoils').then((r) => j<SpoilsList>(r)),
  rebuild: (id: string, order: number[]) => post(`/api/raids/spoils/${encodeURIComponent(id)}`, { order }).then((r) => j<RebuildReply>(r)),
  seen: (id: string) => post(`${at(id)}/seen`).then((r) => j<{ ok: true }>(r)),
};

/**
 * The army just built, handed from the armoury to `/raid/<id>` so its loading starts from the `POST`'s cards with
 * no second read. In memory only: a reload resumes through `GET /api/raids/:id`.
 */
let handed: LoadingRaid | undefined;
export function handRaid(raid: LoadingRaid): void {
  handed = raid;
}
export function takeRaid(id: string): LoadingRaid | undefined {
  const r = handed?.id === id ? handed : undefined;
  handed = undefined;
  return r;
}
